import { useEffect, useState, useRef, useCallback } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, Upload, Sparkles, Download, RefreshCw, ArrowLeft, X, Wand2, Check, Instagram, ClipboardPaste, Palette, History, Trash2, Pencil, Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useFeatures, isFeatureEnabled } from "@/lib/features";
import { readClipboardImageFile, isSupportedImage } from "@/lib/clipboardImage";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const ASSIST_ACTIONS = [
  { k: "fix_grammar", l: "Fix Grammar" }, { k: "spell_check", l: "Spell Check" },
  { k: "shorter", l: "Shorter" }, { k: "crisp", l: "Crisp" },
  { k: "creative", l: "Creative" }, { k: "professional", l: "Professional" },
  { k: "catchy", l: "Catchy" }, { k: "rewrite", l: "Rewrite" },
  { k: "headline", l: "Headline" }, { k: "caption", l: "Caption" }, { k: "hook", l: "Hook" },
];

const STYLE_LABELS = {
  auto: "Auto (best fit)", bold_modern: "Bold & Modern", minimal: "Minimal", cinematic: "Cinematic",
  vibrant: "Vibrant", editorial: "Editorial", luxury: "Luxury", gradient: "Gradient", retro: "Retro", "3d": "3D",
};

// Field with inline AI rewrite assistant + OCR (paste image → text).
function AssistField({ label, testid, value, onChange, textarea, placeholder, ocr }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const ocrRef = useRef(null);
  const run = async (action) => {
    setBusy(action); setSuggestion(null);
    try { const { suggestion: s } = await api.igTextAssist(action, value); setSuggestion({ action, text: s }); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI Assist failed."); }
    setBusy(null);
  };
  const extractFrom = async (file) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Not a supported image."); return; }
    setOcrBusy(true);
    try {
      const { text } = await api.igOcr(file);
      if (!text || !text.trim()) toast.error("No readable text found in the image.");
      else { onChange(value && value.trim() ? `${value.trim()}\n${text.trim()}` : text.trim()); toast.success("Text extracted from image"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Text extraction failed."); }
    setOcrBusy(false);
  };
  const pasteOcr = async () => {
    try { const f = await readClipboardImageFile(); await extractFrom(f); }
    catch (e) { toast.error(e?.message || "Nothing to paste."); }
  };
  const Field = textarea ? "textarea" : "input";
  return (
    <div data-testid={`igv3-field-${testid}`}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-1">
        <label className="text-xs font-semibold text-[#A1A1AA]">{label}</label>
        <div className="flex items-center gap-1.5">
          {ocr && (
            <>
              <input ref={ocrRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`igv3-ocr-upload-${testid}`}
                onChange={(e) => { extractFrom(e.target.files?.[0]); e.target.value = ""; }} />
              <button type="button" onClick={pasteOcr} disabled={ocrBusy} data-testid={`igv3-ocr-paste-${testid}`}
                className="flex items-center gap-1 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-0.5 text-[10px] font-bold text-[#7fe9f2] hover:bg-[#00F0FF]/20 disabled:opacity-50">
                {ocrBusy ? <Loader2 size={11} className="animate-spin" /> : <ClipboardPaste size={11} />} Paste → Text</button>
              <button type="button" onClick={() => ocrRef.current?.click()} disabled={ocrBusy} data-testid={`igv3-ocr-btn-${testid}`}
                className="flex items-center gap-1 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-0.5 text-[10px] font-bold text-[#7fe9f2] hover:bg-[#00F0FF]/20 disabled:opacity-50"><Upload size={11} /> Image</button>
            </>
          )}
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`igv3-assist-toggle-${testid}`}
            className="flex items-center gap-1 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-2 py-0.5 text-[10px] font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20"><Wand2 size={11} /> AI Assist</button>
        </div>
      </div>
      <Field value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`igv3-input-${testid}`}
        rows={textarea ? 4 : undefined}
        className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
      {open && (
        <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.03] p-2" data-testid={`igv3-assist-panel-${testid}`}>
          <div className="flex flex-wrap gap-1.5">
            {ASSIST_ACTIONS.map((a) => (
              <button key={a.k} type="button" onClick={() => run(a.k)} disabled={!!busy} data-testid={`igv3-assist-${a.k}-${testid}`}
                className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-40">
                {busy === a.k ? <Loader2 size={11} className="animate-spin" /> : a.l}
              </button>
            ))}
          </div>
          {suggestion && (
            <div className="mt-2 rounded-lg border border-[#8B5CF6]/30 bg-black/40 p-2" data-testid={`igv3-assist-suggestion-${testid}`}>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#a78bfa]">Suggestion</div>
              <div className="whitespace-pre-wrap text-sm text-white">{suggestion.text}</div>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => { onChange(suggestion.text); setSuggestion(null); toast.success("Applied"); }} data-testid={`igv3-assist-use-${testid}`}
                  className="flex items-center gap-1 rounded-lg bg-[#8B5CF6] px-2.5 py-1 text-[11px] font-bold text-white"><Check size={12} /> Use</button>
                <button type="button" onClick={() => setSuggestion(null)} className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-semibold text-[#A1A1AA]">Keep Original</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Compact single-image slot (upload / paste / remove) → routes through igUpload.
function ImageSlot({ label, testid, role, value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Not a supported image."); return; }
    setBusy(true);
    try { const res = await api.igUpload(file, role); onChange(res); toast.success(`${label} added`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    setBusy(false);
  };
  const paste = async () => {
    try { const f = await readClipboardImageFile(); await send(f); }
    catch (e) { toast.error(e?.message || "Nothing to paste."); }
  };
  return (
    <div data-testid={`igv3-imageslot-${testid}`}>
      <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">{label}</label>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => { send(e.target.files?.[0]); e.target.value = ""; }} data-testid={`igv3-imageslot-input-${testid}`} />
      {value ? (
        <div className="relative overflow-hidden rounded-lg border border-white/10">
          <img src={abs(value.url)} alt={label} className="h-24 w-full object-contain bg-black/40" />
          <button type="button" onClick={() => onChange(null)} data-testid={`igv3-imageslot-remove-${testid}`}
            className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-red-600"><X size={12} /></button>
        </div>
      ) : (
        <div className="flex gap-1.5">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`igv3-imageslot-upload-${testid}`}
            className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 py-3 text-[11px] font-semibold text-[#A1A1AA] hover:border-[#00F0FF]/50 disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload</button>
          <button type="button" onClick={paste} disabled={busy} data-testid={`igv3-imageslot-paste-${testid}`}
            className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 px-2 py-3 text-[11px] font-semibold text-[#A1A1AA] hover:border-[#00F0FF]/50 disabled:opacity-50"><ClipboardPaste size={13} /></button>
        </div>
      )}
    </div>
  );
}

export default function InstagramPostMakerV3({ preview, v4Mode }) {
  useFeatures();
  const galleryOn = isFeatureEnabled("instagram_post_maker_gallery");
  const [cfg, setCfg] = useState(null);
  const [aspect, setAspect] = useState("4:5");
  const [style, setStyle] = useState("auto");
  const [category, setCategory] = useState("");
  const [mood, setMood] = useState("");
  const [headline, setHeadline] = useState("");
  const [text, setText] = useState("");
  const [caption, setCaption] = useState("");
  const [prompt, setPrompt] = useState("");
  const [colors, setColors] = useState([]);
  const [newColor, setNewColor] = useState("#00F0FF");
  const [main, setMain] = useState(null);
  const [logo, setLogo] = useState(null);
  const [refs, setRefs] = useState([]);
  const [keepPhoto, setKeepPhoto] = useState(!!v4Mode);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmDel, setConfirmDel] = useState(null);
  const [cdOpen, setCdOpen] = useState(false);
  const [cdBusy, setCdBusy] = useState(false);
  const [cdText, setCdText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [gq, setGq] = useState("");
  const [gSort, setGSort] = useState("newest");
  const [gDate, setGDate] = useState("");
  const [gPage, setGPage] = useState(1);
  const [gMeta, setGMeta] = useState({ total: 0, pages: 1 });

  const refreshPosts = useCallback(() => {
    api.igMyPosts({ page: gPage, page_size: 24, sort: gSort, q: gq, date: gDate })
      .then((r) => { setPosts(r.posts || []); setGMeta({ total: r.total || 0, pages: r.pages || 1 }); })
      .catch(() => {});
  }, [gPage, gSort, gq, gDate]);

  useEffect(() => {
    api.igConfig().then((c) => {
      setCfg(c);
      const styles = c.v3_styles ? Object.keys(c.v3_styles) : Object.keys(STYLE_LABELS);
      if (!styles.includes(style)) setStyle(styles[0]);
    }).catch(() => setCfg({ aspects: [{ key: "4:5", w: 1080, h: 1350 }], v3_styles: STYLE_LABELS }));
    api.igGetBrandKit().then((k) => { if (k?.colors?.length) setColors(k.colors.slice(0, 5)); }).catch(() => {});
    refreshPosts();
  }, []); // eslint-disable-line

  // Re-fetch the gallery when search / sort / date / page change (search is debounced).
  useEffect(() => {
    const t = setTimeout(() => refreshPosts(), gq ? 350 : 0);
    return () => clearTimeout(t);
  }, [gPage, gSort, gDate, gq, refreshPosts]);
  // Any filter change resets to page 1.
  useEffect(() => { setGPage(1); }, [gq, gSort, gDate]);

  // Load a past post's inputs back into the form for tweak + regenerate.
  const loadPost = (post) => {
    const inp = post.inputs || {};
    setAspect(inp.aspect || "4:5");
    setStyle(inp.style || "auto");
    setCategory(inp.category || "");
    setMood(inp.mood || "");
    setHeadline(inp.headline || "");
    setText(inp.text || "");
    setCaption(inp.caption || "");
    setPrompt(inp.prompt || "");
    setColors(inp.brand_colors || []);
    setMain(inp.main_path ? { path: inp.main_path, url: `/api/media?path=${inp.main_path}` } : null);
    setLogo(inp.logo_path ? { path: inp.logo_path, url: `/api/media?path=${inp.logo_path}` } : null);
    setRefs((inp.ref_paths || []).map((p) => ({ path: p, url: `/api/media?path=${p}` })));
    setResult({ generation_id: post.id, url: post.result_url, path: post.result_path, aspect: post.aspect, ms: post.ms || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Loaded — tweak anything and Regenerate");
  };

  const deletePost = async (id, e) => {
    e.stopPropagation();
    setConfirmDel({ ids: [id] });
  };

  // Reference-image paste from clipboard (same upload pipeline as the upload button).
  const pasteRef = async () => {
    if (refs.length >= 4) { toast.error("Maximum 4 reference images."); return; }
    try { const f = await readClipboardImageFile(); await addRef(f); }
    catch (e) { toast.error(e?.message || "Clipboard image access isn't available. You can upload an image instead."); }
  };

  // Extra creative direction — AI Assistant
  const cdContext = () => ({
    category, headline, text, caption, mood, style, aspect,
    brand_colors: colors, existing: prompt, has_main: !!main, has_logo: !!logo,
  });
  const runCd = async (instruction) => {
    setCdBusy(true);
    try { const { suggestion } = await api.igCreativeDirection(instruction, cdContext()); setCdText(suggestion || ""); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI Assistant couldn't generate a suggestion. You can enter your creative direction manually."); }
    setCdBusy(false);
  };
  const openCd = () => { setCdOpen(true); setCdText(""); runCd("Suggest a strong creative direction for this post."); };
  const useCd = () => { if (cdText.trim()) { setPrompt(cdText.trim()); toast.success("Creative direction applied"); } setCdOpen(false); };

  // Gallery selection (stable IDs)
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(posts.map((p) => p.id)));
  const clearSel = () => setSelected(new Set());
  const selIds = () => posts.filter((p) => selected.has(p.id)).map((p) => p.id);
  const bulkDownload = async () => {
    const ids = selIds(); if (!ids.length) return;
    setBulkBusy(true);
    try {
      const blob = await api.igBulkDownloadPosts(ids);
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = "caption-motion-posts.zip"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed."); }
    setBulkBusy(false);
  };
  const confirmBulkDelete = async () => {
    const ids = confirmDel?.ids || []; if (!ids.length) { setConfirmDel(null); return; }
    setBulkBusy(true);
    try {
      if (ids.length === 1) await api.igDeletePost(ids[0]); else await api.igBulkDeletePosts(ids);
      setPosts((p) => p.filter((x) => !ids.includes(x.id)));
      setSelected((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; });
      toast.success(`Deleted ${ids.length} post${ids.length > 1 ? "s" : ""}`);
    } catch { toast.error("Delete failed."); }
    setBulkBusy(false); setConfirmDel(null);
  };

  const addRef = async (file) => {
    if (!file || !isSupportedImage(file.type)) return;
    try { const res = await api.igUpload(file, "reference"); setRefs((r) => [...r, res].slice(0, 4)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };
  const refInput = useRef(null);

  const generate = useCallback(async () => {
    if (![headline, text, caption, prompt].some((v) => (v || "").trim()) && !main && !logo) {
      toast.error("Add a headline, some text, an image or a prompt first."); return;
    }
    const useV4 = keepPhoto && !!main;
    if (keepPhoto && !main) { toast.error("Upload your Main Subject Image to keep it exactly."); return; }
    setBusy(true); setResult(null);
    try {
      let res;
      if (useV4) {
        const composed = [
          (prompt || "").trim(),
          headline.trim() ? `Headline: ${headline.trim()}` : "",
          text.trim() ? `Body text: ${text.trim()}` : "",
        ].filter(Boolean).join("\n");
        res = await api.igGenerate({
          version: "v4", output_type: "normal_post", platform: "instagram",
          aspect, headline, text, caption, prompt: composed, override_prompt: composed,
          main_path: main.path, logo_path: logo?.path || null,
        });
      } else {
        res = await api.igGenerate({
          version: "v3", template_source: "none", output_type: "normal_post",
          aspect, style, mood, category, headline, text, caption, prompt,
          brand_colors: colors,
          logo_path: logo?.path || null, main_path: main?.path || null,
          ref_paths: refs.map((r) => r.path),
        });
      }
      setResult(res);
      toast.success(useV4 ? "Post generated — your photo was used as the real input!" : "Post generated!");
      refreshPosts();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object") ? (d.message || `${d.stage || ""} ${d.provider_error || ""}`.trim()) : d;
      toast.error(msg || "Generation failed. Try adjusting your inputs.");
    }
    setBusy(false);
  }, [aspect, style, mood, category, headline, text, caption, prompt, colors, logo, main, refs, keepPhoto]);

  const download = async () => {
    if (!result) return;
    try { await api.igRegisterDownload(result.generation_id); } catch { /* non-blocking */ }
    const a = document.createElement("a");
    a.href = abs(result.url); a.download = `caption-motion-post-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!cfg) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#E1306C]" /></div>;
  const aspects = cfg.aspects || [{ key: "4:5", w: 1080, h: 1350 }];
  const styles = cfg.v3_styles ? Object.keys(cfg.v3_styles) : Object.keys(STYLE_LABELS);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="ig-post-maker-v3">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <a href="/tools" className="mb-2 inline-flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-white"><ArrowLeft size={13} /> Tools</a>
            <h1 className="flex items-center gap-2 text-2xl font-black"><Instagram size={22} className="text-[#E1306C]" /> AI Post Maker</h1>
            <p className="mt-1 text-sm text-[#A1A1AA]">No templates. Describe your post and let AI design it from scratch.{preview ? " (admin preview)" : ""}</p>
            {v4Mode && <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#00F0FF]/15 px-3 py-1 text-xs font-bold text-[#00F0FF]" data-testid="igv3-v4-banner">Version 4 · Google image-input — your uploaded photo is kept as the real input</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
          {/* ------- Inputs ------- */}
          <div className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            {/* Format + style */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Format</label>
                <Select value={aspect} onValueChange={setAspect}>
                  <SelectTrigger data-testid="igv3-aspect" className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] text-white">
                    {aspects.map((a) => <SelectItem key={a.key} value={a.key}>{a.key} · {a.w}×{a.h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Visual style</label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger data-testid="igv3-style" className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] text-white">
                    {styles.map((s) => <SelectItem key={s} value={s}>{STYLE_LABELS[s] || s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Topic / category (optional)</label>
                <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Cricket, Food, Tech" data-testid="igv3-category"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Mood / tone (optional)</label>
                <input value={mood} onChange={(e) => setMood(e.target.value)} placeholder="e.g. energetic, calm, premium" data-testid="igv3-mood"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
              </div>
            </div>

            <AssistField label="Headline" testid="headline" value={headline} onChange={setHeadline} placeholder="The dominant text on the post" ocr />
            <AssistField label="Body / information" testid="body" value={text} onChange={setText} textarea placeholder="Supporting text, details, stats…" ocr />
            <AssistField label="Small caption / footer (optional)" testid="caption" value={caption} onChange={setCaption} placeholder="e.g. @yourbrand · date" ocr />

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-semibold text-[#A1A1AA]">Extra creative direction (optional)</label>
                <button type="button" onClick={openCd} data-testid="igv3-cd-assist-btn"
                  className="flex items-center gap-1 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-2 py-0.5 text-[10px] font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20"><Wand2 size={11} /> AI Assistant</button>
              </div>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} placeholder="Any specific look, layout or elements you want" data-testid="igv3-prompt"
                className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
            </div>

            {/* Brand colours */}
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-[#A1A1AA]"><Palette size={12} /> Brand colours (optional)</label>
              <div className="flex flex-wrap items-center gap-2">
                {colors.map((c, i) => (
                  <span key={i} className="flex items-center gap-1 rounded-full border border-white/10 bg-black/40 py-0.5 pl-1 pr-2 text-xs" data-testid={`igv3-color-${i}`}>
                    <span className="h-4 w-4 rounded-full border border-white/20" style={{ background: c }} />{c}
                    <button type="button" onClick={() => setColors(colors.filter((_, j) => j !== i))} className="text-[#A1A1AA] hover:text-red-400"><X size={11} /></button>
                  </span>
                ))}
                {colors.length < 5 && (
                  <div className="flex items-center gap-1">
                    <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-7 w-7 cursor-pointer rounded border border-white/10 bg-transparent" data-testid="igv3-color-picker" />
                    <button type="button" onClick={() => { if (!colors.includes(newColor)) setColors([...colors, newColor]); }} data-testid="igv3-color-add"
                      className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-white hover:border-[#00F0FF]/50">Add</button>
                  </div>
                )}
              </div>
            </div>

            {/* Images */}
            <div className="grid grid-cols-2 gap-3">
              <ImageSlot label="Main subject (optional)" testid="main" role="main" value={main} onChange={setMain} />
              <ImageSlot label="Logo (optional)" testid="logo" role="logo" value={logo} onChange={setLogo} />
            </div>

            {v4Mode && cfg.v4_enabled && (
              <label className={`flex items-start gap-3 rounded-xl border p-3 ${keepPhoto ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 bg-black/20"} ${!main ? "opacity-60" : ""}`} data-testid="igv3-keep-photo">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#00F0FF]" checked={keepPhoto} onChange={(e) => setKeepPhoto(e.target.checked)} disabled={!cfg.v4_key_configured} data-testid="igv3-keep-photo-toggle" />
                <div>
                  <div className="text-sm font-semibold text-white">Keep my photo exactly <span className="ml-1 rounded bg-[#00F0FF]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#00F0FF]">GOOGLE IMAGE-INPUT</span></div>
                  <div className="mt-0.5 text-xs text-[#A1A1AA]">
                    {cfg.v4_key_configured
                      ? "Edits your uploaded photo directly (keeps the real subject) using your Google model — instead of generating a brand-new image. Upload a Main subject image and describe the edit in the prompt."
                      : "Add your Google image API key in Admin → Version Control → V4 to enable this."}
                  </div>
                  {keepPhoto && !main && <div className="mt-1 text-xs font-semibold text-amber-400">Upload a Main subject image above to use this.</div>}
                </div>
              </label>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Reference images (optional, up to 4)</label>
              <div className="flex flex-wrap gap-2">
                {refs.map((r, i) => (
                  <div key={i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-white/10" data-testid={`igv3-ref-${i}`}>
                    <img src={abs(r.url)} alt="ref" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setRefs(refs.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white hover:bg-red-600"><X size={10} /></button>
                  </div>
                ))}
                {refs.length < 4 && (
                  <>
                    <input ref={refInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { addRef(e.target.files?.[0]); e.target.value = ""; }} data-testid="igv3-ref-input" />
                    <button type="button" onClick={() => refInput.current?.click()} data-testid="igv3-ref-add" title="Upload"
                      className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-white/20 text-[#A1A1AA] hover:border-[#00F0FF]/50"><Upload size={16} /></button>
                    <button type="button" onClick={pasteRef} data-testid="igv3-ref-paste" title="Paste from clipboard"
                      className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-white/20 text-[10px] font-semibold text-[#A1A1AA] hover:border-[#00F0FF]/50"><ClipboardPaste size={15} /> Paste</button>
                  </>
                )}
              </div>
            </div>

            <button type="button" onClick={generate} disabled={busy} data-testid="igv3-generate-btn"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#E1306C] to-[#8B5CF6] py-3 text-sm font-black text-white shadow-lg hover:brightness-110 disabled:opacity-50">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Designing your post…</> : <><Sparkles size={16} /> Generate Post</>}
            </button>
          </div>

          {/* ------- Result ------- */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="igv3-result-panel">
            <div className="mb-3 text-sm font-bold text-white">Preview</div>
            {busy && (
              <div className="flex h-80 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-white/15 text-[#A1A1AA]" data-testid="igv3-generating">
                <Loader2 size={28} className="animate-spin text-[#E1306C]" />
                <div className="text-sm">Designing from scratch with AI…</div>
              </div>
            )}
            {!busy && result && (
              <div className="space-y-3" data-testid="igv3-result">
                <img src={abs(result.url)} alt="Generated post" className="w-full rounded-xl border border-white/10" data-testid="igv3-result-image" />
                <div className="flex gap-2">
                  <button type="button" onClick={download} data-testid="igv3-download-btn"
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#00F0FF] py-2.5 text-sm font-bold text-black hover:brightness-110"><Download size={15} /> Download</button>
                  <button type="button" onClick={generate} data-testid="igv3-regenerate-btn"
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-white/15 px-3 py-2.5 text-sm font-semibold text-white hover:bg-white/10"><RefreshCw size={15} /> Regenerate</button>
                </div>
                <p className="text-center text-[11px] text-[#666]">Generated in {(result.ms / 1000).toFixed(1)}s · {result.aspect}</p>
              </div>
            )}
            {!busy && !result && (
              <div className="flex h-80 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 text-center text-[#A1A1AA]" data-testid="igv3-empty">
                <Sparkles size={26} className="text-[#8B5CF6]" />
                <div className="text-sm">Your AI-designed post appears here</div>
                <div className="max-w-[240px] text-xs text-[#666]">Fill in a headline or paste an image with text, pick a style, then hit Generate.</div>
              </div>
            )}
          </div>
        </div>

        {/* ------- Post history gallery ------- */}
        {galleryOn && (posts.length > 0 || gMeta.total > 0 || gq || gDate) && (
          <div className="mt-8" data-testid="igv3-history">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-bold text-white"><History size={16} className="text-[#8B5CF6]" /> Your posts <span className="text-xs font-normal text-[#666]">({gMeta.total})</span></div>
              {selected.size > 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/[0.06] px-2.5 py-1.5 text-xs" data-testid="igv3-selection-toolbar">
                  <span className="font-bold text-[#00F0FF]" data-testid="igv3-sel-count">{selected.size} selected</span>
                  <span className="text-[#444]">|</span>
                  <button type="button" onClick={selectAll} data-testid="igv3-select-all" className="font-semibold text-[#A1A1AA] hover:text-white">Select all</button>
                  <button type="button" onClick={bulkDownload} disabled={bulkBusy} data-testid="igv3-bulk-download" className="flex items-center gap-1 font-semibold text-white hover:text-[#00F0FF] disabled:opacity-50"><Download size={13} /> Download</button>
                  <button type="button" onClick={() => setConfirmDel({ ids: selIds() })} disabled={bulkBusy} data-testid="igv3-bulk-delete" className="flex items-center gap-1 font-semibold text-red-400 hover:text-red-300 disabled:opacity-50"><Trash2 size={13} /> Delete</button>
                  <button type="button" onClick={clearSel} data-testid="igv3-clear-sel" className="font-semibold text-[#A1A1AA] hover:text-white">Clear</button>
                </div>
              ) : (
                <button type="button" onClick={selectAll} data-testid="igv3-select-all-empty" className="text-xs font-semibold text-[#A1A1AA] hover:text-white">Select</button>
              )}
            </div>
            {/* Search / sort / calendar toolbar */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#666]" />
                <input value={gq} onChange={(e) => setGq(e.target.value)} placeholder="Search your posts…" data-testid="igv3-gallery-search"
                  className="w-full rounded-lg border border-white/10 bg-black/30 py-2 pl-8 pr-3 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
              </div>
              <select value={gSort} onChange={(e) => setGSort(e.target.value)} data-testid="igv3-gallery-sort"
                className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <input type="date" value={gDate} onChange={(e) => setGDate(e.target.value)} data-testid="igv3-gallery-date"
                className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60 [color-scheme:dark]" />
              {(gq || gDate) && (
                <button type="button" onClick={() => { setGq(""); setGDate(""); }} data-testid="igv3-gallery-clear-filters"
                  className="rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-[#A1A1AA] hover:text-white">Clear</button>
              )}
            </div>
            {posts.length === 0 ? (
              <div className="rounded-xl border border-white/10 bg-black/20 py-10 text-center text-sm text-[#666]" data-testid="igv3-gallery-empty">No posts match your filters.</div>
            ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {posts.map((p) => {
                const isSel = selected.has(p.id);
                return (
                <div key={p.id} className={`group relative overflow-hidden rounded-xl border bg-black/40 ${isSel ? "border-[#00F0FF] ring-2 ring-[#00F0FF]/40" : "border-white/10"}`} data-testid={`igv3-history-item-${p.id}`}>
                  <img src={abs(p.result_url)} alt="post" className="aspect-square w-full object-cover" loading="lazy" />
                  <button type="button" onClick={() => toggleSel(p.id)} data-testid={`igv3-sel-${p.id}`}
                    className={`absolute left-1.5 top-1.5 z-20 flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${isSel ? "border-[#00F0FF] bg-[#00F0FF] text-black" : "border-white/60 bg-black/50 text-transparent hover:border-[#00F0FF]"}`}>
                    <Check size={12} />
                  </button>
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 opacity-0 transition-opacity group-hover:opacity-100">
                    <button type="button" onClick={() => loadPost(p)} data-testid={`igv3-history-open-${p.id}`}
                      className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-2.5 py-1.5 text-[11px] font-bold text-black hover:brightness-110"><Pencil size={12} /> Reopen & Edit</button>
                    <a href={abs(p.result_url)} download target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 rounded-lg border border-white/30 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-white/10"><Download size={12} /> Download</a>
                  </div>
                  <button type="button" onClick={(e) => deletePost(p.id, e)} data-testid={`igv3-history-delete-${p.id}`}
                    className="absolute right-1 top-1 z-20 rounded-full bg-black/70 p-1 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"><Trash2 size={12} /></button>
                  {p.inputs?.headline && <div className="truncate px-1.5 py-1 text-[10px] text-[#A1A1AA]">{p.inputs.headline}</div>}
                </div>
                );
              })}
            </div>
            )}
            {gMeta.pages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2" data-testid="igv3-gallery-pagination">
                <button type="button" disabled={gPage <= 1} onClick={() => setGPage((p) => Math.max(1, p - 1))} data-testid="igv3-gallery-prev"
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-40">Prev</button>
                <span className="text-xs text-[#A1A1AA]" data-testid="igv3-gallery-pageinfo">Page {gPage} of {gMeta.pages}</span>
                <button type="button" disabled={gPage >= gMeta.pages} onClick={() => setGPage((p) => Math.min(gMeta.pages, p + 1))} data-testid="igv3-gallery-next"
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-40">Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Creative-direction AI Assistant modal */}
      {cdOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="igv3-cd-modal" onClick={() => setCdOpen(false)}>
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#121212] p-5" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="mb-2 flex items-center gap-2 text-sm font-black text-white"><Wand2 size={16} className="text-[#a78bfa]" /> Creative Direction Assistant</div>
            <p className="mb-3 text-xs text-[#A1A1AA]">Visual guidance only — it never invents facts. Based on your current inputs.</p>
            <div className="min-h-[96px] rounded-lg border border-white/10 bg-black/40 p-3 text-sm text-white" data-testid="igv3-cd-suggestion">
              {cdBusy ? <span className="flex items-center gap-2 text-[#A1A1AA]"><Loader2 size={14} className="animate-spin" /> Thinking…</span> : (cdText || <span className="text-[#666]">No suggestion yet.</span>)}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {[["Try again", "Suggest a different creative direction."], ["Shorter", "Make it shorter and punchier."], ["More premium", "Make it feel more premium and luxurious."], ["More dramatic", "Make it more dramatic and cinematic."]].map(([l, instr]) => (
                <button key={l} type="button" disabled={cdBusy} onClick={() => runCd(cdText ? `${instr} Current: ${cdText}` : instr)} data-testid={`igv3-cd-${l.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-40">{l}</button>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setCdOpen(false)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA]">Cancel</button>
              <button type="button" onClick={useCd} disabled={cdBusy || !cdText.trim()} data-testid="igv3-cd-use" className="flex items-center gap-1.5 rounded-lg bg-[#8B5CF6] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"><Check size={14} /> Use this</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="igv3-delete-confirm" onClick={() => setConfirmDel(null)}>
          <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#121212] p-6" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="font-black text-white">Delete {confirmDel.ids.length} post{confirmDel.ids.length > 1 ? "s" : ""}?</div>
            <p className="mt-2 text-sm text-[#A1A1AA]">This action cannot be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDel(null)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA]">Cancel</button>
              <button type="button" onClick={confirmBulkDelete} disabled={bulkBusy} data-testid="igv3-delete-confirm-btn" className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
